import {Box, Link, Text, Token, Octicon} from '@primer/react'
import {graphql, useQueryLoader, type PreloadedQuery, usePreloadedQuery, useFragment} from 'react-relay'
import type {TrackedByQuery} from './__generated__/TrackedByQuery.graphql'
import {IssueClosedIcon, IssueOpenedIcon, SkipIcon} from '@primer/octicons-react'
import {useEffect} from 'react'
import type {IssueStateReason, TrackedByFragment$key} from './__generated__/TrackedByFragment.graphql'

type TrackedByProps = {
  issueId: string
  url: string
}

type TrackingIssue = {
  number: number
  url: string
  stateReason?: IssueStateReason | null
}

type TrackedByFetchedProps = {
  trackedByKey: TrackedByFragment$key
  url: string
}

type TrackedByInternalProps = {
  queryRef: PreloadedQuery<TrackedByQuery>
  url: string
}

export const TrackedByFragment = graphql`
  fragment TrackedByFragment on Issue {
    ... on Issue {
      trackedInIssues(first: 10) {
        edges {
          node {
            number
            url
            stateReason
          }
        }
        totalCount
      }
    }
  }
`

export const TrackedByGraphQLQuery = graphql`
  query TrackedByQuery($issueId: ID!) {
    node(id: $issueId) {
      ... on Issue {
        ...TrackedByFragment
      }
    }
  }
`

export function TrackedBy({issueId, url}: TrackedByProps) {
  const [queryRef, loadQueryRef] = useQueryLoader<TrackedByQuery>(TrackedByGraphQLQuery)

  useEffect(() => {
    loadQueryRef({issueId}, {fetchPolicy: 'store-only'})
  }, [loadQueryRef, issueId])

  if (!queryRef) {
    return null
  }

  return <TrackedByInternal queryRef={queryRef} url={url} />
}

export function TrackedByInternal({queryRef, url}: TrackedByInternalProps) {
  const data = usePreloadedQuery<TrackedByQuery>(TrackedByGraphQLQuery, queryRef)
  if (!data.node) {
    return null
  }
  return <TrackedByFetched trackedByKey={data.node} url={url} />
}

function TrackedByFetched({trackedByKey, url}: TrackedByFetchedProps) {
  const data = useFragment(TrackedByFragment, trackedByKey)

  const numberOfTrackingIssues = data.trackedInIssues?.totalCount ?? 0
  if (numberOfTrackingIssues === 0) return null

  const trackingIssues = data.trackedInIssues?.edges?.flatMap(a => (a && a.node ? a.node : [])) ?? []

  const trackingItem =
    trackingIssues.length === 1
      ? getSingleTrackingItem(trackingIssues[0]!, url)
      : getMultipleTrackingItems(trackingIssues, url)

  const tokenContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      {trackingItem}
    </Box>
  )

  return (
    <Box>
      <Token
        text={tokenContent}
        size={'xlarge'}
        sx={{
          px: 'var(--control-small-paddingInline-normal)',
          backgroundColor: 'canvas.default',
          fontWeight: '400',
          fontSize: 0,
          gap: 1,
        }}
      />
    </Box>
  )
}

function getIssueIcon(stateReason?: IssueStateReason | null) {
  if (stateReason === 'COMPLETED') {
    return {
      color: 'done.fg',
      icon: IssueClosedIcon,
    }
  } else if (stateReason === 'NOT_PLANNED') {
    return {
      color: 'fg.muted',
      icon: SkipIcon,
    }
  } else {
    return {
      color: 'open.fg',
      icon: IssueOpenedIcon,
    }
  }
}

function getSingleTrackingItem(trackingIssue: TrackingIssue, url: string) {
  const icon = getIssueIcon(trackingIssue.stateReason)

  return (
    <>
      <Text
        data-hovercard-url={`${url}/tracked_in/hovercard/`}
        sx={{
          marginRight: '4px',
        }}
      >
        Tracked by
      </Text>
      <Box
        key={trackingIssue.number}
        data-hovercard-url={`${trackingIssue.url}/hovercard`}
        sx={{display: 'flex', alignItems: 'center', gap: '2px'}}
      >
        <Octicon icon={icon.icon} size={16} sx={{color: icon.color}} />
        <Link href={trackingIssue.url}>{`#${trackingIssue.number}`}</Link>
      </Box>
    </>
  )
}

function getMultipleTrackingItems(trackingIssues: TrackingIssue[], url: string) {
  return <Text data-hovercard-url={`${url}/tracked_in/hovercard/`}>{`Tracked by ${trackingIssues.length} issues`}</Text>
}

try{ TrackedBy.displayName ||= 'TrackedBy' } catch {}
try{ TrackedByInternal.displayName ||= 'TrackedByInternal' } catch {}
try{ TrackedByFetched.displayName ||= 'TrackedByFetched' } catch {}